import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCategories } from '../../services/api';
import { ChevronRight, Grid } from 'lucide-react';

const MobileCategories = ({ listings = [], user }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const categoriesData = await getCategories();
      
      // Direct access to categories data
      const categories = categoriesData.data || categoriesData || [];
      
      // Filter to show only main categories (parent_id === null)
      const mainCategories = categories.filter(cat => cat.parent_id === null);
      setCategories(mainCategories);
      
    } catch (err) {
      // console.error('❌ Error loading categories:', err);
      setError('Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadCategories();
  }, []);

  // Memoize filtered listings
  const filteredListings = useMemo(() => {
    if (!listings || !Array.isArray(listings)) return [];
    
    let filtered = [...listings];
    
    // Filter out current user's listings if user is logged in
    if (user?.user_id) {
      filtered = filtered.filter(
        (item) => item.seller_id !== user.user_id && item.user_id !== user.user_id
      );
    }
    
    return filtered;
  }, [listings, user?.user_id]);

  // Memoize category count calculation
  const getCategoryCount = useMemo(() => {
    return (categoryId) => {
      if (filteredListings.length === 0) return 0;
      
      const category = categories.find(cat => cat.id === parseInt(categoryId));
      if (!category) return 0;

      // Get all subcategories
      const subcategories = categories.filter(cat => cat.parent_id === category.id);
      const subcategoryIds = subcategories.map(sub => sub.id);
      const allCategoryIds = [category.id, ...subcategoryIds];
      
      // Count listings from all related categories
      const count = filteredListings.filter(listing => 
        allCategoryIds.includes(parseInt(listing.category_id))
      ).length;
      
      return count;
    };
  }, [filteredListings, categories]);

  // Memoize popular categories with counts
  const popularCategories = useMemo(() => {
    return categories
      .map(category => ({
        ...category,
        count: getCategoryCount(category.id)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Show top 10
  }, [categories, getCategoryCount]);

  const handleSeeAllPress = () => {
    navigate('/categories');
  };

  const handleCategoryPress = (category) => {
    navigate(`/category/${category.id}`, { state: { category } });
  };

  const handleAllPress = () => {
    navigate('/categories');
  };

  const getCategoryIcon = (iconName) => {
    const iconMap = {
      'hardware-chip': '💻',
      'phone-portrait': '📱',
      'car': '🚗',
      'shirt': '👕',
      'sparkles': '✨',
      'heart': '❤️',
      'restaurant': '🍴',
      'business': '🏢',
      'home': '🏠',
      'paw': '🐾',
      'construct': '🔧',
      'laptop': '💻',
      'tv': '📺',
      'camera': '📷',
      'game-controller': '🎮',
      'hardware-chip-outline': '💻',
      'print': '🖨️',
      'flash': '⚡',
      'tablet-portrait': '📱',
      'ear': '🎧',
      'watch': '⌚',
      'headset': '🎧',
      'car-sport': '🏎️',
      'bicycle': '🚲',
      'bus': '🚌',
      'build': '🔧',
      'disc': '💿',
      'boat': '🚤',
      'man': '👨',
      'woman': '👩',
      'footsteps': '👟',
      'bag': '👜',
      'diamond': '💎',
      'shirt-outline': '👔',
      'glasses': '👓',
      'basketball': '🏀',
      'water': '💧',
      'cut': '✂️',
      'map': '🗺️',
    };
    
    return iconMap[iconName] || '📦';
  };

  if (loading) {
    return (
      <div className="px-4 py-3 bg-white border-b border-gray-100">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center">
            <div className="p-1.5 bg-gray-200 rounded-lg mr-2 animate-pulse">
              <Grid className="w-4 h-4 text-transparent" />
            </div>
            <div className="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="h-5 w-16 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="flex space-x-2 overflow-x-auto pb-1">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-9 bg-gray-200 rounded-lg animate-pulse flex-shrink-0 w-36"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 py-3 bg-white border-b border-gray-100">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center">
            <div className="p-1.5 bg-[#fff5e6] rounded-lg mr-2">
              <Grid className="w-4 h-4 text-[#ff660e]" />
            </div>
            <h2 className="text-base font-bold text-gray-900">Categories</h2>
          </div>
          <button className="text-sm font-medium text-[#ff660e]">
            All
          </button>
        </div>
        <div className="text-sm text-center py-3 text-gray-600 bg-gray-50 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="px-4 py-3 bg-white border-b border-gray-100">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center">
            <div className="p-1.5 bg-[#fff5e6] rounded-lg mr-2">
              <Grid className="w-4 h-4 text-[#ff660e]" />
            </div>
            <h2 className="text-base font-bold text-gray-900">Categories</h2>
          </div>
          <button className="text-sm font-medium text-[#ff660e]">
            All
          </button>
        </div>
        <div className="text-sm text-center py-3 text-gray-600 bg-gray-50 rounded-lg">
          No categories
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-3 bg-white border-b border-gray-100">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center">
          <div className="p-1.5 bg-[#ff660e] rounded-lg mr-2">
            <Grid className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Categories</h2>
        </div>
        <button 
          onClick={handleSeeAllPress}
          className="text-sm font-medium text-[#ff660e] hover:text-[#e55a0d]"
        >
          View All
        </button>
      </div>
      
      {/* SINGLE ROW: Horizontal Scrollable Categories - Icon first, then name */}
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {/* "All" Category */}
        <button 
          onClick={handleAllPress}
          className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-[#fff5e6] border border-[#ffd9b3] rounded-lg hover:bg-[#ffeed9] transition-colors"
        >
          <span className="text-lg">🌐</span>
          <span className="text-sm font-medium text-[#ff660e]">All</span>
        </button>
        
        {/* Popular Categories - SINGLE ROW: Icon first, then name */}
        {popularCategories.map(category => (
          <button
            key={category.id}
            onClick={() => handleCategoryPress(category)}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-[#fff5e6] hover:border-[#ffd9b3] transition-all group"
          >
            <span 
              className="text-lg group-hover:scale-110 transition-transform"
              style={{ color: category.icon_color || '#ff660e' }}
            >
              {getCategoryIcon(category.icon_name)}
            </span>
            <span className="text-sm font-medium text-gray-900 group-hover:text-[#ff660e] whitespace-nowrap">
              {category.name}
            </span>
          </button>
        ))}
      </div>
      
      {/* No Listings Message */}
      {filteredListings.length === 0 && categories.length > 0 && (
        <div className="mt-3 p-2 bg-[#fff5e6] border border-[#ffd9b3] rounded-lg">
          <p className="text-xs font-medium text-[#ff660e] text-center">
            No listings available yet
          </p>
        </div>
      )}
    </div>
  );
};

export default MobileCategories;